import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router';
import { compose } from 'react-apollo';
import { connect } from 'react-redux';
import BodyContentLayout from './layout/BodyContentLayout';
import LeftContentLayout from './layout/LeftContentLayout';
import RightContentLayout from './layout/RightContentLayout';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import ResourceItem from './ResourceItem';
import CreateAndAssignAction from 'chameleon-packages/units/components/actions/CreateAndAssignAction';
import AddToQuizAction from 'chameleon-packages/units/components/actions/AddToQuizAction';
import { getTopicQuestionsFilter } from 'legacy-packages/unit-outline/src/components/_shared/util';
import {
  setDynamicItems,
  removeDynamicItems,
} from 'chameleon-src/components/QuestionPreviewWrapper/state/actions';
import { RESOURCE_TYPE_KEYS } from 'chameleon/ui-stack/ui/units/subunit-types/SubunitTypes';

const TopicQuestions = ({
  title,
  label,
  router,
  params,
  items,
  unitNumber,
  unitTitle,
  subunitNumber,
  subunitTitle,
  widgetTagColumn,
  unitsLabel,
  metadata,
  showIcon,
  setDynamicItemsAction,
  removeDynamicItemsAction,
  questionPreviewProps,
  isHoverAvailable,
  subunitLabel,
}) => {
  const { location } = router;
  const { query } = location;
  const { subject: subjectId } = params;
  const assignableItems = items.filter(
    (item) => item.hasAllTopicsCovered === true,
  );

  useEffect(() => {
    if (isHoverAvailable && assignableItems.length) {
      setDynamicItemsAction(
        unitNumber,
        subunitNumber,
        assignableItems.map((item) => ({
          ...item,
          item_id: item.itemId,
          learnosity_item_reference: item.accNum,
        })),
      );
    }

    return () => {
      if (isHoverAvailable) {
        removeDynamicItemsAction(unitNumber, subunitNumber);
      }
    };
  }, [isHoverAvailable]);

  const topicQuestionsFilter = getTopicQuestionsFilter({
    subjectId,
    unitNumber,
    metadata,
    widgetTagColumn,
    skipCustomFilters: false,
    unitTitle,
    unitsLabel,
    subunitLabel,
  });

  const QUESTION_TYPE_LABEL = {
    'questions/free_response': 'FRQ',
    'questions/multiple_choice': 'MCQ',
  };

  return (
    <Fragment>
      <BodyContentLayout data-type={RESOURCE_TYPE_KEYS['topic-questions']}>
        <RightContentLayout
          title={title}
          showIcon={showIcon}
          label={label}
          icon="topic_questions"
        >
          <div className="ml-32">
            <CreateAndAssignAction
              items={assignableItems}
              subunitTitle={subunitTitle}
              unitNumber={unitNumber}
              subunitNumber={subunitNumber}
            />
          </div>
        </RightContentLayout>

        <LeftContentLayout className="divide-y divide-solid divide-gray-400">
          {assignableItems.map((item) => {
            const { itemId, title, type, accNum } = item;
            const questionTypeLabel = QUESTION_TYPE_LABEL[type];
            const fullTitle = questionTypeLabel
              ? `${title} (${questionTypeLabel})`
              : title;
            return (
              <ResourceItem
                key={itemId}
                iconName="topic_question_indicator"
                className="hover-question-parent"
              >
                <A11y>
                  <Link
                    onlyActiveOnIndex={false}
                    aria-label={fullTitle}
                    tabIndex={0}
                    to={{
                      pathname: `/${subjectId}/home/preview/item/${itemId}`,
                      query: {
                        ...query,
                        previewItemId: itemId,
                        unit: unitNumber,
                        subunit: subunitNumber,
                      },
                    }}
                    data-itemid={itemId}
                    data-reference={accNum}
                    {...questionPreviewProps}
                  >
                    <span className="font-medium hover:underline hover:text-black text-black text-base">
                      {fullTitle}
                    </span>
                  </Link>
                </A11y>
                <div className="ml-auto">
                  <AddToQuizAction
                    item={item}
                    router={router}
                    unitNumber={unitNumber}
                    unitTitle={unitTitle}
                    subunitTitle={subunitTitle}
                    widgetTagColumn={widgetTagColumn}
                    unitsLabel={unitsLabel}
                    subjectId={Number.parseInt(subjectId, 10)}
                    metadata={metadata}
                    topicQuestionsFilter={topicQuestionsFilter}
                  />
                </div>
              </ResourceItem>
            );
          })}
        </LeftContentLayout>
      </BodyContentLayout>
    </Fragment>
  );
};

TopicQuestions.propTypes = {
  title: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  unitNumber: PropTypes.string.isRequired,
  subunitNumber: PropTypes.string,
  subunitTitle: PropTypes.string.isRequired,
  unitTitle: PropTypes.string.isRequired,
  widgetTagColumn: PropTypes.string,
  unitsLabel: PropTypes.string,
  metadata: PropTypes.string,
  showIcon: PropTypes.bool,
};

TopicQuestions.defaultProps = {
  title: 'Topic Questions',
  label: '',
  widgetTagColumn: '',
  unitsLabel: '',
  metadata: '',
  showIcon: true,
  subunitNumber: '',
};

const mapStateToProps = ({
  featureFlag,
  userSubjects,
  userData,
  questionPreview,
}) => ({
  featureFlag,
  userSubjects,
  userData,
  questionPreviewProps: questionPreview.questionProps,
  isHoverAvailable: featureFlag.hometq_preview_approach_gama,
});

const mapDispatchToProps = {
  setDynamicItemsAction: setDynamicItems,
  removeDynamicItemsAction: removeDynamicItems,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(TopicQuestions);
